import { cn } from '@/lib/cn';

export type ArtworkName = 'table' | 'cloche';

/**
 * The picture slot on welcome screens. Today it draws a line illustration in the brand colours; a venue
 * photo can take its place later by passing `photo`, without changing the screens that use it.
 * Decorative: the screen's text says everything, so the drawing is hidden from screen readers.
 */
export function Artwork({
  name,
  photo,
  className,
}: {
  name: ArtworkName;
  photo?: { src: string; alt: string };
  className?: string;
}) {
  if (photo) {
    return (
      <img
        src={photo.src}
        alt={photo.alt}
        className={cn('h-full w-full rounded-surface object-cover', className)}
      />
    );
  }
  const Drawing = name === 'table' ? TableDrawing : ClocheDrawing;
  return <Drawing className={cn('h-auto w-full', className)} />;
}

const INK = 'var(--color-primary-deep)';
const LINE = {
  stroke: INK,
  strokeWidth: 3,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/** A served plate under a cloche, a QR table card being scanned, a glass: "scan, and it comes to you". */
function TableDrawing({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 360 260" aria-hidden="true" focusable="false" className={className}>
      <circle cx="196" cy="132" r="112" fill="var(--color-accent-soft)" />
      <circle cx="96" cy="70" r="26" fill="var(--color-primary-soft)" />

      {/* QR table card with scan brackets */}
      <path d="M118 120 L134 214" {...LINE} fill="none" />
      <rect x="56" y="112" width="62" height="102" rx="8" fill="#fff" {...LINE} />
      <QrMarks x={67} y={126} />
      <path
        d="M52 96 h-8 v8 M122 96 h8 v8 M52 230 h-8 v-8 M122 230 h8 v-8"
        stroke="var(--color-primary)"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />

      {/* Plate and cloche */}
      <ellipse cx="216" cy="214" rx="74" ry="9" fill="#fff" {...LINE} />
      <path d="M156 206 a60 60 0 0 1 120 0 Z" fill="#fff" {...LINE} />
      <path
        d="M174 190 a44 44 0 0 1 26 -32"
        stroke="var(--color-primary)"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="216" cy="140" r="7" fill="var(--color-primary)" {...LINE} />
      <Sparkle x={292} y={118} />
      <Sparkle x={168} y={112} small />

      {/* Glass */}
      <path
        d="M300 150 h28 c0 24 -6 36 -14 36 c-8 0 -14 -12 -14 -36 z"
        fill="var(--color-primary-soft)"
        {...LINE}
      />
      <path d="M314 186 v28 M304 214 h20" {...LINE} fill="none" />

      {/* Table edge */}
      <path d="M24 226 H336" {...LINE} fill="none" />
    </svg>
  );
}

/** A plate under a cloche: the welcome before the menu. */
function ClocheDrawing({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 240 140" aria-hidden="true" focusable="false" className={className}>
      <ellipse cx="120" cy="78" rx="96" ry="58" fill="var(--color-accent-soft)" />
      <ellipse cx="120" cy="118" rx="70" ry="8" fill="#fff" {...LINE} />
      <path d="M64 110 a56 56 0 0 1 112 0 Z" fill="#fff" {...LINE} />
      <path
        d="M81 96 a41 41 0 0 1 24 -30"
        stroke="var(--color-primary)"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="120" cy="48" r="6" fill="var(--color-primary)" {...LINE} />
      <path d="M30 126 H210" {...LINE} fill="none" />
      <Sparkle x={196} y={40} />
      <Sparkle x={46} y={56} small />
    </svg>
  );
}

/** A QR-like pattern: three finder squares and a few modules. Not a real code. */
function QrMarks({ x, y }: { x: number; y: number }) {
  const finder = (fx: number, fy: number) => (
    <g key={`${fx}-${fy}`}>
      <rect x={fx} y={fy} width="14" height="14" rx="2" fill="none" stroke={INK} strokeWidth="3" />
      <rect x={fx + 4.5} y={fy + 4.5} width="5" height="5" fill={INK} />
    </g>
  );
  const modules = [
    [22, 2],
    [28, 8],
    [22, 14],
    [2, 22],
    [10, 28],
    [22, 22],
    [28, 28],
    [34, 22],
    [22, 34],
    [34, 34],
  ];
  return (
    <g transform={`translate(${x} ${y})`}>
      {finder(0, 0)}
      {finder(26, 0)}
      {finder(0, 26)}
      {modules.map(([mx, my]) => (
        <rect key={`${mx}-${my}`} x={mx} y={my} width="5" height="5" fill={INK} />
      ))}
      <rect x="0" y="52" width="40" height="4" rx="2" fill="var(--color-line-strong)" />
      <rect x="6" y="62" width="28" height="4" rx="2" fill="var(--color-line-strong)" />
    </g>
  );
}

function Sparkle({ x, y, small = false }: { x: number; y: number; small?: boolean }) {
  const r = small ? 5 : 8;
  return (
    <path
      d={`M${x} ${y - r} V${y + r} M${x - r} ${y} H${x + r}`}
      stroke="var(--color-accent)"
      strokeWidth="3"
      strokeLinecap="round"
    />
  );
}
